import type { PortfolioItem } from '../services/portfolio';
import type { Category } from '../services/categories';
import { isVideoProject } from './portfolio-media';

export type PortfolioMediaTile = {
  id: string;
  kind: 'photo' | 'video';
  imageUrl: string;
  videoUrl?: string;
  sourceItem: PortfolioItem;
};

export type CategoryTileGroup = {
  id: string;
  name: string;
  slug: string;
  tiles: PortfolioMediaTile[];
  accentIndex: number;
};

/** Décompose les projets en médias individuels (photos + vidéos). */
export function itemToMediaTiles(item: PortfolioItem): PortfolioMediaTile[] {
  if (isVideoProject(item) && item.videoUrl) {
    return [
      {
        id: `${item.id}-video`,
        kind: 'video',
        imageUrl: item.coverUrl,
        videoUrl: item.videoUrl,
        sourceItem: item,
      },
    ];
  }

  const urls: string[] = [];
  if (item.coverUrl) urls.push(item.coverUrl);
  item.galleryUrls?.filter(Boolean).forEach((url) => {
    if (!urls.includes(url)) urls.push(url);
  });

  return urls.map((url, index) => ({
    id: `${item.id}-photo-${index}`,
    kind: 'photo' as const,
    imageUrl: url,
    sourceItem: item,
  }));
}

export function itemsToMediaTiles(items: PortfolioItem[]): PortfolioMediaTile[] {
  return items.flatMap(itemToMediaTiles);
}

export function groupTilesByCategory(
  items: PortfolioItem[],
  categories: Category[],
): CategoryTileGroup[] {
  const byCategory = new Map<string, PortfolioItem[]>();

  for (const item of items) {
    const catId = item.categoryId || 'uncategorized';
    if (!byCategory.has(catId)) byCategory.set(catId, []);
    byCategory.get(catId)!.push(item);
  }

  const result: CategoryTileGroup[] = [];

  categories.forEach((cat, index) => {
    const catItems = byCategory.get(cat.id);
    if (!catItems?.length) return;
    const tiles = itemsToMediaTiles(catItems);
    if (!tiles.length) return;
    result.push({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      tiles,
      accentIndex: index,
    });
  });

  const uncategorized = byCategory.get('uncategorized');
  if (uncategorized?.length) {
    const tiles = itemsToMediaTiles(uncategorized);
    if (tiles.length) {
      result.push({
        id: 'uncategorized',
        name: 'Autres réalisations',
        slug: 'autres',
        tiles,
        accentIndex: result.length,
      });
    }
  }

  return result;
}

export function countMediaTiles(items: PortfolioItem[]): number {
  return itemsToMediaTiles(items).length;
}

/** Texte alt SEO pour images portfolio. */
export function getMediaAlt(tile: PortfolioMediaTile): string {
  const item = tile.sourceItem;
  const category = item.categoryObject?.name || 'événementiel';
  const label = item.artist ? `${item.artist} – ${item.title}` : item.title;

  if (tile.kind === 'video') {
    return `Vidéo ${category} — ${label} — L Benna Production Tunis`;
  }
  return `Photo ${category} — ${label} — L Benna Production Tunis`;
}
